import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Christmas Canvas Component
function ChristmasCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let snowflakes = [];
            let lights = [];
            let gifts = [];
            let stars = [];
            let santaSleigh = null;
            let textParticles = [];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // [HELPER FUNCTIONS - Factory Pattern]

            // Helper: Create Snowflake
            const createSnowflake = () => ({
                x: Math.random() * canvas.width,
                y: -10,
                radius: Math.random() * 3 + 1,
                speed: Math.random() * 1 + 0.5,
                drift: Math.random() * 0.5 - 0.25,
                opacity: Math.random() * 0.6 + 0.4,
                wobble: Math.random() * Math.PI * 2,
                wobbleSpeed: Math.random() * 0.05 + 0.02,
            });

            const updateSnowflake = (flake) => {
                flake.wobble += flake.wobbleSpeed;
                flake.x += Math.sin(flake.wobble) * flake.drift;
                flake.y += flake.speed;
                if (flake.y > canvas.height) {
                    flake.y = -10;
                    flake.x = Math.random() * canvas.width;
                }
                if (flake.x < 0) flake.x = canvas.width;
                if (flake.x > canvas.width) flake.x = 0;
            };

            const drawSnowflake = (flake) => {
                ctx.save();
                ctx.globalAlpha = flake.opacity;
                ctx.beginPath();
                ctx.shadowBlur = 5;
                ctx.shadowColor = '#FFFFFF';
                for (let i = 0; i < 6; i += 1) {
                    ctx.save();
                    ctx.translate(flake.x, flake.y);
                    ctx.rotate((Math.PI * 2 * i) / 6);
                    ctx.moveTo(0, 0);
                    ctx.lineTo(0, flake.radius);
                    ctx.moveTo(0, flake.radius * 0.6);
                    ctx.lineTo(-flake.radius * 0.3, flake.radius * 0.8);
                    ctx.moveTo(0, flake.radius * 0.6);
                    ctx.lineTo(flake.radius * 0.3, flake.radius * 0.8);
                    ctx.restore();
                }
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
            };

            // Helper: Create Christmas Light
            const createChristmasLight = (x, y, color) => ({
                x,
                y,
                color,
                radius: 4,
                glowSize: 15,
                pulse: Math.random() * Math.PI * 2,
                pulseSpeed: Math.random() * 0.05 + 0.03,
            });

            const updateChristmasLight = (light) => {
                light.pulse += light.pulseSpeed;
            };

            const drawChristmasLight = (light) => {
                const intensity = Math.sin(light.pulse) * 0.3 + 0.7;
                ctx.save();
                const gradient = ctx.createRadialGradient(
                    light.x, light.y, 0,
                    light.x, light.y, light.glowSize
                );
                const alphaHex = Math.floor(intensity * 255).toString(16).padStart(2, '0');
                gradient.addColorStop(0, `${light.color}${alphaHex}`);
                gradient.addColorStop(0.5, `${light.color}44`);
                gradient.addColorStop(1, `${light.color}00`);
                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(light.x, light.y, light.glowSize, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(light.x, light.y, light.radius, 0, Math.PI * 2);
                ctx.fillStyle = light.color;
                ctx.shadowBlur = 10;
                ctx.shadowColor = light.color;
                ctx.fill();
                ctx.beginPath();
                ctx.arc(light.x - 1, light.y - 1, light.radius / 2, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Gift Box
            const createGiftBox = (x, y, size, color) => ({
                x,
                y,
                size,
                color,
                ribbonColor: '#FFD700',
                floatOffset: Math.random() * Math.PI * 2,
                floatSpeed: 0.02,
                rotation: (Math.random() - 0.5) * 0.2,
            });

            const updateGiftBox = (gift) => {
                gift.floatOffset += gift.floatSpeed;
            };

            const drawGiftBox = (gift) => {
                ctx.save();
                ctx.translate(gift.x, gift.y + Math.sin(gift.floatOffset) * 5);
                ctx.rotate(gift.rotation);
                ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
                ctx.fillRect(-gift.size / 2 + 2, -gift.size / 2 + 2, gift.size, gift.size);
                ctx.fillStyle = gift.color;
                ctx.fillRect(-gift.size / 2, -gift.size / 2, gift.size, gift.size);
                ctx.fillStyle = gift.ribbonColor;
                ctx.fillRect(-gift.size / 6, -gift.size / 2, gift.size / 3, gift.size);
                ctx.fillRect(-gift.size / 2, -gift.size / 6, gift.size, gift.size / 3);
                ctx.beginPath();
                ctx.arc(-gift.size / 4, -gift.size / 2, gift.size / 6, 0, Math.PI * 2);
                ctx.arc(gift.size / 4, -gift.size / 2, gift.size / 6, 0, Math.PI * 2);
                ctx.fillStyle = gift.ribbonColor;
                ctx.fill();
                ctx.beginPath();
                ctx.arc(0, -gift.size / 2, gift.size / 8, 0, Math.PI * 2);
                ctx.fillStyle = '#FF0000';
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Star
            const createStar = () => ({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height * 0.5,
                size: Math.random() * 2 + 1,
                twinkle: Math.random() * Math.PI * 2,
                twinkleSpeed: Math.random() * 0.05 + 0.02,
            });

            const updateStar = (star) => {
                star.twinkle += star.twinkleSpeed;
            };

            const drawStar = (star) => {
                const alphaVal = Math.sin(star.twinkle) * 0.5 + 0.5;
                ctx.save();
                ctx.globalAlpha = alphaVal;
                ctx.fillStyle = '#FFFFFF';
                ctx.shadowBlur = 5;
                ctx.shadowColor = '#FFFFFF';
                ctx.beginPath();
                for (let i = 0; i < 4; i += 1) {
                    const angle = (Math.PI * i) / 2;
                    const x = star.x + Math.cos(angle) * star.size;
                    const y = star.y + Math.sin(angle) * star.size;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Santa Sleigh
            const createSantaSleigh = () => ({
                x: -200,
                y: canvas.height * 0.15,
                speed: 3,
                visible: false,
                cooldown: 0,
            });

            const updateSantaSleigh = (sleigh) => {
                if (sleigh.cooldown > 0) {
                    sleigh.cooldown -= 1;
                    return;
                }
                if (!sleigh.visible && Math.random() < 0.002) {
                    sleigh.visible = true;
                    sleigh.x = -200;
                }
                if (sleigh.visible) {
                    sleigh.x += sleigh.speed;
                    if (sleigh.x > canvas.width + 200) {
                        sleigh.visible = false;
                        sleigh.cooldown = 300;
                    }
                }
            };

            const drawSantaSleigh = (sleigh) => {
                if (!sleigh.visible) return;
                ctx.save();
                ctx.globalAlpha = 0.9;
                ctx.fillStyle = '#8B0000';
                ctx.beginPath();
                ctx.moveTo(sleigh.x, sleigh.y);
                ctx.quadraticCurveTo(sleigh.x - 20, sleigh.y + 10, sleigh.x - 30, sleigh.y + 5);
                ctx.lineTo(sleigh.x + 40, sleigh.y + 5);
                ctx.quadraticCurveTo(sleigh.x + 50, sleigh.y + 10, sleigh.x + 40, sleigh.y);
                ctx.closePath();
                ctx.fill();
                ctx.fillStyle = '#FF0000';
                ctx.fillRect(sleigh.x + 15, sleigh.y - 15, 10, 15);
                ctx.beginPath();
                ctx.arc(sleigh.x + 20, sleigh.y - 18, 6, 0, Math.PI * 2);
                ctx.fillStyle = '#FFD7BE';
                ctx.fill();
                ctx.fillStyle = '#8B4513';
                ctx.fillRect(sleigh.x - 15, sleigh.y - 5, 8, 8);
                for (let i = 0; i < 5; i += 1) {
                    const trailX = sleigh.x - i * 15;
                    const trailY = sleigh.y + Math.sin((sleigh.x + i * 30) * 0.1) * 5;
                    ctx.beginPath();
                    ctx.arc(trailX, trailY, 2, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(255, 215, 0, ${1 - i * 0.2})`;
                    ctx.fill();
                }
                ctx.restore();
            };

            // Helper: Create Text Particle
            const createTextParticle = (x, y, char, index) => ({
                x,
                y: y - 100,
                targetY: y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
                rotation: (Math.random() - 0.5) * Math.PI,
                rotationSpeed: (Math.random() - 0.5) * 0.02,
                color: index % 2 === 0 ? '#FF0000' : '#228B22',
                pulse: 0,
                pulseSpeed: 0.05,
            });

            const updateTextParticle = (tp) => {
                tp.time += 1;
                if (tp.time > tp.delay) {
                    if (tp.y < tp.targetY) tp.y += 8;
                    if (tp.rotation !== 0) tp.rotation *= 0.95;
                    tp.alpha = Math.min(1, tp.alpha + 0.1);
                    tp.scale = Math.min(1, tp.scale + 0.1);
                    tp.pulse += tp.pulseSpeed;
                }
            };

            const drawTextParticle = (tp) => {
                if (tp.time < tp.delay) return;
                const pulseScale = 1 + Math.sin(tp.pulse) * 0.05;
                ctx.save();
                ctx.globalAlpha = tp.alpha;
                ctx.translate(tp.x, tp.y);
                ctx.rotate(tp.rotation);
                ctx.scale(tp.scale * pulseScale, tp.scale * pulseScale);
                ctx.font = 'bold 56px "Arial", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowBlur = 20;
                ctx.shadowColor = tp.color;
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 4;
                ctx.strokeText(tp.char, 0, 0);
                const gradient = ctx.createLinearGradient(0, -20, 0, 20);
                gradient.addColorStop(0, tp.color);
                gradient.addColorStop(1, tp.color === '#FF0000' ? '#8B0000' : '#006400');
                ctx.fillStyle = gradient;
                ctx.fillText(tp.char, 0, 0);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
                ctx.font = 'bold 54px "Arial", sans-serif';
                ctx.fillText(tp.char, -1, -1);
                ctx.restore();
            };

            // Action: Initialize elements
            const initializeElements = () => {
                snowflakes = [];
                for (let i = 0; i < 150; i += 1) snowflakes.push(createSnowflake());
                stars = [];
                for (let i = 0; i < 30; i += 1) stars.push(createStar());
                lights = [];
                const lightColors = ['#FF0000', '#00FF00', '#0000FF', '#FFD700', '#FF69B4', '#00CED1'];
                const lightCount = Math.floor(canvas.width / 80);
                for (let i = 0; i < lightCount; i += 1) {
                    const x = (canvas.width / lightCount) * i + canvas.width / (lightCount * 2);
                    const y = 30 + Math.sin((i * Math.PI) / 3) * 20;
                    const color = lightColors[i % lightColors.length];
                    lights.push(createChristmasLight(x, y, color));
                }
                gifts = [];
                const giftColors = ['#FF0000', '#228B22', '#0000FF', '#FFD700', '#FF69B4'];
                const giftPositions = canvas.width < 768
                    ? [{ x: canvas.width * 0.15, y: canvas.height * 0.75, size: 30 }]
                    : [
                        { x: canvas.width * 0.12, y: canvas.height * 0.78, size: 35 },
                        { x: canvas.width * 0.88, y: canvas.height * 0.78, size: 35 },
                    ];
                giftPositions.forEach((pos, index) => {
                    const color = giftColors[index % giftColors.length];
                    gifts.push(createGiftBox(pos.x, pos.y, pos.size, color));
                });
                santaSleigh = createSantaSleigh();
            };

            // Action: Create Text
            const createChristmasText = () => {
                const text = 'MERRY CHRISTMAS';
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.20;
                const charSpacing = canvas.width < 768 ? 28 : 42;
                const startX = centerX - (text.length * charSpacing) / 2;
                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    const char = text[i];
                    const charX = startX + i * charSpacing;
                    textParticles.push(createTextParticle(charX, centerY, char, i));
                }
            };

            initializeElements();
            setTimeout(() => createChristmasText(), 50);

            const animate = () => {
                ctx.fillStyle = 'rgba(15, 23, 42, 0.1)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                stars.forEach(s => { updateStar(s); drawStar(s); });
                snowflakes.forEach(s => { updateSnowflake(s); drawSnowflake(s); });
                lights.forEach(l => { updateChristmasLight(l); drawChristmasLight(l); });
                gifts.forEach(g => { updateGiftBox(g); drawGiftBox(g); });
                if (santaSleigh) { updateSantaSleigh(santaSleigh); drawSantaSleigh(santaSleigh); }
                textParticles.forEach(tp => { updateTextParticle(tp); drawTextParticle(tp); });
                animationFrameId = requestAnimationFrame(animate);
            };
            animate();

            const handleResize = () => {
                resizeCanvas();
                initializeElements();
                if (textParticles.length > 0) createChristmasText();
            };
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

// Main Christmas Theme Component with Default Export
export default function ChristmasTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
                px: { xs: 2, md: 15 },
                backgroundImage: 'url(/assets/background/festival_christmas_bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                overflow: 'hidden',
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(135deg, ${alpha('#0F172A', 0.85)}, ${alpha('#1E3A5F', 0.75)})`,
                    zIndex: 0,
                },
            }}
        >
            <ChristmasCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(5,15,5,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha('#1B5E20', 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.45)}`,
                    `0 16px 64px ${alpha('#1B5E20', 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: 'linear-gradient(90deg, transparent, #B71C1C, #1B5E20, #FFD700, transparent)',
                    borderRadius: '0 0 4px 4px',
                    animation: 'xmasGlow 3s ease-in-out infinite',
                },
                '@keyframes xmasGlow': { '0%, 100%': { opacity: 0.7 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: 'linear-gradient(90deg, #B71C1C, #1B5E20, #FFD700, #B71C1C)',
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha('#B71C1C', 0.5)}`,
                    }}>🎄 &nbsp; Merry Christmas &nbsp; 🎄</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}>
                    <Logo />
                </Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>

            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha('#B71C1C', 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

ChristmasTheme.propTypes = {
    children: PropTypes.node,
};
